/**
 * Radio Report Parser Service
 * Handles API calls to the Python backend for report parsing
 */

// Python backend URL - can be configured via environment variable

const RADIO_REPORT_API_URL =
  (import.meta as any).env?.VITE_RADIO_REPORT_API_URL ||
  'https://report.clearscan.ca';

// Validation constants
export const MAX_FILE_SIZE_MB = 5;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
export const MAX_FILES_COUNT = 10;
export const ALLOWED_FILE_EXTENSIONS = [
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.bmp',
  '.tiff',
  '.gif',
  '.heic',
  '.heif',
  '.docx',
];
export const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/bmp',
  'image/tiff',
  'image/gif',
  'image/heic',
  'image/heif',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

export interface UploadedFileInfo {
  file_id: string;
  filename: string;
  text_preview: string;
  file_path: string;
  file_type: string;
}

export interface UploadResponse {
  session_id: string;
  uploaded_files: UploadedFileInfo[];
  total_files: number;
}

export interface PatientInfo {
  name?: string;
  mrn?: string;
  dob?: string;
  age?: string;
  sex?: string;
  status?: string;
}

export interface MergedReport {
  patient?: PatientInfo;
  [key: string]: unknown;
}

export interface ParseAndGenerateResponse {
  session_id: string;
  merged_report: MergedReport;
  filename: string;
  download_url: string;
  total_files_processed: number;
}

export interface FileValidationResult {
  isValid: boolean;
  error?: string;
}

export interface FilesValidationResult {
  validFiles: File[];
  errors: { filename: string; error: string }[];
}

/**
 * Custom error class for API errors with additional context
 */
export class RadioReportError extends Error {
  public readonly statusCode?: number;
  public readonly errorType: 'validation' | 'network' | 'server' | 'unknown';
  public readonly details?: string;

  constructor(
    message: string,
    errorType: 'validation' | 'network' | 'server' | 'unknown' = 'unknown',
    statusCode?: number,
    details?: string
  ) {
    super(message);
    this.name = 'RadioReportError';
    this.errorType = errorType;
    this.statusCode = statusCode;
    this.details = details;
  }
}

class RadioReportService {
  private baseURL: string;

  constructor(baseURL: string = RADIO_REPORT_API_URL) {
    this.baseURL = baseURL;
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  /**
   * Validate a single file
   */
  validateFile(file: File): FileValidationResult {
    // Check file size
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return {
        isValid: false,
        error: `File size (${this.formatFileSize(file.size)}) exceeds the maximum allowed size of ${MAX_FILE_SIZE_MB}MB`,
      };
    }

    // Check file size is not zero
    if (file.size === 0) {
      return {
        isValid: false,
        error: 'File is empty (0 bytes)',
      };
    }

    // Check file extension
    const ext = '.' + (file.name.split('.').pop()?.toLowerCase() || '');
    if (!ALLOWED_FILE_EXTENSIONS.includes(ext)) {
      return {
        isValid: false,
        error: `File type "${ext}" is not supported. Allowed types: PDF, Images (JPG, PNG, HEIC, etc.), Word documents (.docx)`,
      };
    }

    // Check MIME type (if available)
    if (
      file.type &&
      !ALLOWED_MIME_TYPES.includes(file.type) &&
      !file.type.startsWith('image/')
    ) {
      // Some browsers may report different MIME types, so we're lenient here
      // but still check for obviously wrong types
      const isLikelyValid =
        file.type.includes('pdf') ||
        file.type.includes('image') ||
        file.type.includes('word') ||
        file.type.includes('document');

      if (!isLikelyValid) {
        return {
          isValid: false,
          error: `Invalid file type detected. Please upload a valid PDF, image, or Word document`,
        };
      }
    }

    // Check filename for invalid characters
    const invalidCharSet = new Set([
      '<',
      '>',
      ':',
      '"',
      '/',
      '\\',
      '|',
      '?',
      '*',
    ]);
    const hasInvalidChars = Array.from(file.name).some(
      char => invalidCharSet.has(char) || char.charCodeAt(0) < 32
    );

    if (hasInvalidChars) {
      return {
        isValid: false,
        error: 'Filename contains invalid characters',
      };
    }

    return { isValid: true };
  }

  /**
   * Validate multiple files
   */
  validateFiles(files: File[]): FilesValidationResult {
    const validFiles: File[] = [];
    const errors: { filename: string; error: string }[] = [];

    // Check total file count
    if (files.length > MAX_FILES_COUNT) {
      errors.push({
        filename: 'Multiple files',
        error: `Too many files selected. Maximum allowed is ${MAX_FILES_COUNT} files at once`,
      });
      return { validFiles: [], errors };
    }

    // Validate each file
    for (const file of files) {
      const result = this.validateFile(file);
      if (result.isValid) {
        validFiles.push(file);
      } else {
        errors.push({
          filename: file.name,
          error: result.error || 'Unknown validation error',
        });
      }
    }

    // Check total size of all files
    const totalSize = validFiles.reduce((sum, file) => sum + file.size, 0);
    const maxTotalSize = MAX_FILE_SIZE_BYTES * 3; // Allow up to 15MB total
    if (totalSize > maxTotalSize) {
      errors.push({
        filename: 'All files',
        error: `Total file size (${this.formatFileSize(totalSize)}) exceeds the maximum allowed total of ${this.formatFileSize(maxTotalSize)}`,
      });
      return { validFiles: [], errors };
    }

    return { validFiles, errors };
  }

  /**
   * Parse error response from the API
   */
  private async parseErrorResponse(response: Response): Promise<string> {
    try {
      const contentType = response.headers.get('content-type');

      if (contentType?.includes('application/json')) {
        const errorData = await response.json();
        // Handle various error response formats
        if (errorData.detail) {
          return typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail);
        }
        if (errorData.message) return errorData.message;
        if (errorData.error) return errorData.error;
        return JSON.stringify(errorData);
      }

      // Try to get text response
      const text = await response.text();
      if (text) return text.substring(0, 200); // Limit length

      return this.getDefaultErrorMessage(response.status);
    } catch {
      return this.getDefaultErrorMessage(response.status);
    }
  }

  /**
   * Get default error message based on HTTP status code
   */
  private getDefaultErrorMessage(statusCode: number): string {
    switch (statusCode) {
      case 400:
        return 'Invalid request. Please check your files and try again.';
      case 401:
        return 'Authentication required. Please log in and try again.';
      case 403:
        return 'Access denied. You do not have permission to perform this action.';
      case 404:
        return 'Service not found. Please try again later.';
      case 408:
        return 'Request timeout. Please try again with smaller files.';
      case 413:
        return 'Files are too large. Please reduce file sizes and try again.';
      case 415:
        return 'Unsupported file type. Please upload PDF, images, or Word documents only.';
      case 422:
        return 'Unable to process the uploaded files. Please check the file format and try again.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Server error. Our team has been notified. Please try again later.';
      case 502:
        return 'Service temporarily unavailable. Please try again in a few minutes.';
      case 503:
        return 'Service is currently under maintenance. Please try again later.';
      case 504:
        return 'Request timeout. The server is taking too long to respond. Please try again.';
      default:
        return `An unexpected error occurred (Error ${statusCode}). Please try again.`;
    }
  }

  /**
   * Upload multiple files to the backend
   */
  async uploadFiles(files: File[]): Promise<UploadResponse> {
    // Validate files before upload
    const validation = this.validateFiles(files);
    if (validation.errors.length > 0) {
      const errorMessages = validation.errors
        .map(e => `${e.filename}: ${e.error}`)
        .join('\n');
      throw new RadioReportError(
        validation.errors.length === 1
          ? validation.errors[0].error
          : `Multiple validation errors:\n${errorMessages}`,
        'validation'
      );
    }

    const formData = new FormData();
    validation.validFiles.forEach(file => {
      formData.append('files', file);
    });

    try {
      const response = await fetch(`${this.baseURL}/api/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorMessage = await this.parseErrorResponse(response);
        throw new RadioReportError(
          errorMessage,
          response.status >= 500 ? 'server' : 'validation',
          response.status
        );
      }

      return response.json();
    } catch (error) {
      if (error instanceof RadioReportError) {
        throw error;
      }

      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new RadioReportError(
          'Unable to connect to the server. Please check your internet connection and try again.',
          'network'
        );
      }

      throw new RadioReportError(
        error instanceof Error
          ? error.message
          : 'Upload failed. Please try again.',
        'unknown'
      );
    }
  }

  /**
   * Parse uploaded files and generate a merged report
   */
  async parseAndGenerate(
    sessionId: string,
    filePaths: string[]
  ): Promise<ParseAndGenerateResponse> {
    if (!sessionId) {
      throw new RadioReportError(
        'Invalid session. Please upload files again.',
        'validation'
      );
    }

    if (!filePaths || filePaths.length === 0) {
      throw new RadioReportError(
        'No files to process. Please upload files first.',
        'validation'
      );
    }

    const formData = new FormData();
    formData.append('session_id', sessionId);
    formData.append('file_paths', JSON.stringify(filePaths));

    try {
      const response = await fetch(`${this.baseURL}/api/parse-and-generate`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorMessage = await this.parseErrorResponse(response);
        throw new RadioReportError(
          errorMessage,
          response.status >= 500 ? 'server' : 'validation',
          response.status
        );
      }

      return response.json();
    } catch (error) {
      if (error instanceof RadioReportError) {
        throw error;
      }

      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new RadioReportError(
          'Lost connection to the server during processing. Please try again.',
          'network'
        );
      }

      throw new RadioReportError(
        error instanceof Error
          ? error.message
          : 'Processing failed. Please try again.',
        'unknown'
      );
    }
  }

  /**
   * Get the download URL for a generated report
   */
  getDownloadUrl(filename: string): string {
    if (!filename) {
      throw new RadioReportError(
        'Invalid filename for download.',
        'validation'
      );
    }
    return `${this.baseURL}/api/download/${encodeURIComponent(filename)}`;
  }

  /**
   * Download a generated report
   */
  async downloadReport(filename: string): Promise<Blob> {
    if (!filename) {
      throw new RadioReportError(
        'Invalid filename for download.',
        'validation'
      );
    }

    try {
      const response = await fetch(
        `${this.baseURL}/api/download/${encodeURIComponent(filename)}`
      );

      if (!response.ok) {
        const errorMessage = await this.parseErrorResponse(response);
        throw new RadioReportError(
          errorMessage,
          response.status >= 500 ? 'server' : 'validation',
          response.status
        );
      }

      return response.blob();
    } catch (error) {
      if (error instanceof RadioReportError) {
        throw error;
      }

      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new RadioReportError(
          'Unable to download the report. Please check your internet connection.',
          'network'
        );
      }

      throw new RadioReportError(
        error instanceof Error ? error.message : 'Failed to download report.',
        'unknown'
      );
    }
  }
}

export const radioReportService = new RadioReportService();
export default radioReportService;
